// ─────────────────────────────────────────────────────────────────────────────
// RENDERER  –  all canvas draw calls live here
// ─────────────────────────────────────────────────────────────────────────────

import {
  Platform, Spike, NEON, NEON2,
  END_FLAG_X, END_FLAG_Y, GROUND_Y, WORLD_WIDTH, ASTRONAUT,
} from '../data/levelData';
import { PhysicsBody }  from './physics';
import { EnemyState, Particle } from './entities';

// ─── Helpers ─────────────────────────────────────────────────────────────────
function hexAlpha(hex: string, a: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
}

function neonRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  fill: string, glow: string, blur = 10,
) {
  ctx.save();
  ctx.shadowColor = glow;
  ctx.shadowBlur  = blur;
  ctx.fillStyle   = fill;
  ctx.fillRect(x, y, w, h);
  ctx.restore();
}

// ─── Parallax background ──────────────────────────────────────────────────────
export function drawBackground(
  ctx: CanvasRenderingContext2D,
  camX: number,
  W: number,
  H: number,
  _time: number,
) {
  const sky = ctx.createLinearGradient(0, 0, 0, H);
  sky.addColorStop(0, '#04040d');
  sky.addColorStop(1, '#0a0a1f');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, W, H);

  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  const seed1 = 13;
  for (let i = 0; i < 140; i++) {
    const sx = ((i * 97 + 11) % WORLD_WIDTH) - camX * 0.05;
    const sy = ((i * 53 + 7)  % (H - 80));
    const wrapped = ((sx % W) + W) % W;
    ctx.fillRect(wrapped, sy, 1, 1);
  }

  for (let i = 0; i < 8; i++) {
    const bx = ((i * 1847 + seed1 * 3) % WORLD_WIDTH) - camX * 0.15;
    const by = 40 + ((i * 211) % (H * 0.6));
    const wrapped = ((bx % W) + W) % W;
    const grad = ctx.createRadialGradient(wrapped, by, 0, wrapped, by, 120 + (i % 3) * 60);
    const col  = i % 2 === 0 ? NEON : NEON2;
    grad.addColorStop(0, hexAlpha(col, 0.04));
    grad.addColorStop(1, 'transparent');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);
  }

  ctx.save();
  ctx.strokeStyle = hexAlpha(NEON, 0.06);
  ctx.lineWidth   = 1;
  const gridOffset = (camX * 0.4) % 80;
  for (let x = -gridOffset; x < W + 80; x += 80) {
    ctx.beginPath();
    ctx.moveTo(x, H * 0.55);
    ctx.lineTo(x - 60, H);
    ctx.stroke();
  }
  for (let row = 0; row < 6; row++) {
    const gy = H * 0.55 + row * (H * 0.45 / 5);
    ctx.globalAlpha = 0.04 + row * 0.01;
    ctx.beginPath();
    ctx.moveTo(0, gy);
    ctx.lineTo(W, gy);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  ctx.restore();

  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.06)';
  for (let y = 0; y < H; y += 4) {
    ctx.fillRect(0, y, W, 1);
  }
  ctx.restore();
}

// ─── Platforms ───────────────────────────────────────────────────────────────
export function drawPlatforms(
  ctx:     CanvasRenderingContext2D,
  platforms: Platform[],
  offsets: Map<number, { ox: number; oy: number }>,
  camX:    number,
  viewW:   number,
  _time:   number,
) {
  for (let i = 0; i < platforms.length; i++) {
    const p  = platforms[i];
    const mo = offsets.get(i);
    const sx = p.x + (mo?.ox ?? 0) - camX;
    const sy = p.y + (mo?.oy ?? 0);

    if (sx + p.w < 0 || sx > viewW) continue;

    const isGround = p.h > 30;
    const col      = p.color ?? '#1e2540';
    const glow     = p.color ?? NEON;

    if (isGround) {
      ctx.fillStyle = col;
      ctx.fillRect(sx, sy, p.w, p.h);
      ctx.fillStyle = hexAlpha(NEON, 0.15);
      ctx.fillRect(sx, sy, p.w, 3);
    } else {
      neonRect(ctx, sx, sy, p.w, p.h, col, glow, p.color ? 12 : 6);
      ctx.save();
      ctx.shadowColor = glow;
      ctx.shadowBlur  = 10;
      ctx.fillStyle   = p.color ?? NEON;
      ctx.fillRect(sx, sy, p.w, 3);
      ctx.restore();

      if (p.label) {
        ctx.save();
        ctx.font         = 'bold 10px monospace';
        ctx.textAlign    = 'center';
        ctx.fillStyle    = '#fff';
        ctx.shadowColor  = glow;
        ctx.shadowBlur   = 8;
        ctx.fillText(p.label, sx + p.w / 2, sy - 6);
        ctx.restore();
      }
    }
  }
}

// ─── Spikes ──────────────────────────────────────────────────────────────────
export function drawSpikes(
  ctx:    CanvasRenderingContext2D,
  spikes: Spike[],
  camX:   number,
) {
  ctx.save();
  ctx.fillStyle   = '#ff003c';
  ctx.shadowColor = '#ff003c';
  ctx.shadowBlur  = 12;
  for (const s of spikes) {
    const sx = s.x - camX;
    const count = Math.ceil(s.w / 16);
    for (let i = 0; i < count; i++) {
      const tx = sx + i * 16 + 8;
      const ty = s.y;
      ctx.beginPath();
      ctx.moveTo(tx - 7, ty);
      ctx.lineTo(tx + 7, ty);
      ctx.lineTo(tx,     ty - 18);
      ctx.closePath();
      ctx.fill();
    }
  }
  ctx.restore();
}

// ─── Enemies ─────────────────────────────────────────────────────────────────
export function drawEnemies(
  ctx:     CanvasRenderingContext2D,
  enemies: EnemyState[],
  camX:    number,
  viewW:   number,
  time:    number,
) {
  for (const e of enemies) {
    if (e.dead) continue;
    const sx = e.x - camX;
    if (sx + e.w < -40 || sx > viewW + 40) continue;

    const pulse = 0.7 + 0.3 * Math.sin(time * 6 + e.id);

    ctx.save();
    ctx.shadowColor = '#ff003c';
    ctx.shadowBlur  = 14 * pulse;

    if (e.type === 'flying') {
      // Drone body
      ctx.fillStyle = '#1a0010';
      ctx.beginPath();
      ctx.ellipse(sx + e.w / 2, e.y + e.h / 2, e.w / 2, e.h / 2, 0, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = `rgba(255,0,60,${pulse})`;
      ctx.lineWidth = 2;
      ctx.stroke();

      // Eye
      ctx.fillStyle = `rgba(255,80,120,${pulse})`;
      ctx.fillRect(sx + e.w / 2 - 4, e.y + e.h / 2 - 3, 8, 6);

      // Wings
      const flap = Math.sin(time * 14 + e.id) * 6;
      ctx.strokeStyle = '#ff4466';
      ctx.beginPath();
      ctx.moveTo(sx, e.y + e.h / 2);
      ctx.lineTo(sx - 10, e.y + e.h / 2 - flap);
      ctx.moveTo(sx + e.w, e.y + e.h / 2);
      ctx.lineTo(sx + e.w + 10, e.y + e.h / 2 + flap);
      ctx.stroke();
    } else {
      ctx.fillStyle = '#2a0010';
      ctx.fillRect(sx, e.y, e.w, e.h);

      ctx.fillStyle = `rgba(255,0,60,${pulse})`;
      ctx.fillRect(sx + 4, e.y + 8, e.w - 8, 12);

      const legOff = Math.sin(time * 8 + e.id) * 3;
      ctx.fillStyle = '#ff003c';
      ctx.fillRect(sx + 4,      e.y + e.h,   6, 8 + legOff);
      ctx.fillRect(sx + e.w - 10, e.y + e.h, 6, 8 - legOff);
    }

    ctx.restore();
  }
}

// ─── Particles ───────────────────────────────────────────────────────────────
export function drawParticles(
  ctx:       CanvasRenderingContext2D,
  particles: Particle[],
  camX:      number,
) {
  for (const p of particles) {
    const alpha = p.life / p.max;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle   = p.col;
    ctx.shadowColor = p.col;
    ctx.shadowBlur  = 8;
    ctx.beginPath();
    ctx.arc(p.x - camX, p.y, p.r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

// ─── Player ──────────────────────────────────────────────────────────────────
export function drawPlayer(
  ctx:   CanvasRenderingContext2D,
  body:  PhysicsBody,
  camX:  number,
  time:  number,
  dead:  boolean,
  hasJetpack: boolean,
  thrusting: boolean,
) {
  const sx = body.x - camX;
  const sy = body.y;

  const pulse = 0.6 + 0.4 * Math.sin(time * 10);

  ctx.save();
  if (dead) ctx.globalAlpha = 0.3;

  // Jetpack pack
  if (hasJetpack) {
    ctx.fillStyle = '#333';
    ctx.fillRect(sx - 6, sy + 14, 8, 18);
    ctx.fillStyle = thrusting ? '#ffe066' : '#888';
    ctx.shadowColor = thrusting ? '#ffe066' : 'transparent';
    ctx.shadowBlur  = thrusting ? 12 : 0;
    ctx.fillRect(sx - 5, sy + 32, 6, thrusting ? 10 + Math.sin(time * 30) * 3 : 4);
  }

  ctx.shadowColor = NEON;
  ctx.shadowBlur  = 16 * pulse;

  ctx.fillStyle = '#0a2020';
  ctx.fillRect(sx, sy, body.w, body.h);

  ctx.strokeStyle = NEON;
  ctx.lineWidth   = 2;
  ctx.strokeRect(sx + 1, sy + 1, body.w - 2, body.h - 2);

  const vx = body.facingRight ? sx + 6 : sx + 4;
  ctx.fillStyle = hexAlpha(NEON, 0.9);
  ctx.fillRect(vx, sy + 8, 14, 8);

  const bob = body.onGround ? Math.sin(time * 12) * 2 : 0;
  ctx.fillStyle = NEON;
  ctx.fillRect(sx + 4,           sy + body.h,     7, 6 + bob);
  ctx.fillRect(sx + body.w - 11, sy + body.h,     7, 6 - bob);

  ctx.restore();
}

// ─── Jetpack pickup ──────────────────────────────────────────────────────────
export function drawJetpackPickup(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  camX: number,
  time: number,
) {
  const sx = x - camX;
  const bob = Math.sin(time * 3) * 5;

  ctx.save();
  ctx.shadowColor = '#ffe066';
  ctx.shadowBlur  = 16;
  ctx.fillStyle   = '#2a2200';
  ctx.fillRect(sx, y + bob, 28, 32);
  ctx.strokeStyle = '#ffe066';
  ctx.lineWidth   = 2;
  ctx.strokeRect(sx, y + bob, 28, 32);
  ctx.fillStyle = '#ffe066';
  ctx.font = 'bold 9px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('JET', sx + 14, y + bob + 14);
  ctx.fillText('PACK', sx + 14, y + bob + 26);
  ctx.restore();
}

// ─── Old astronaut NPC ───────────────────────────────────────────────────────
export function drawAstronaut(
  ctx: CanvasRenderingContext2D,
  camX: number,
  time: number,
  showPrompt: boolean,
) {
  const n = ASTRONAUT;
  const sx = n.x - camX;
  const bob = Math.sin(time * 2) * 2;

  ctx.save();
  // Suit
  ctx.shadowColor = '#c8c8e0';
  ctx.shadowBlur  = 8;
  ctx.fillStyle   = '#2a2a3a';
  ctx.fillRect(sx, n.y + bob, n.w, n.h);

  // Helmet
  ctx.fillStyle = '#8899aa';
  ctx.beginPath();
  ctx.arc(sx + n.w / 2, n.y + bob + 12, 14, 0, Math.PI * 2);
  ctx.fill();

  // Visor (aged amber)
  ctx.fillStyle = 'rgba(255,180,80,0.7)';
  ctx.fillRect(sx + 8, n.y + bob + 8, 20, 10);

  // Beard hint
  ctx.fillStyle = '#ddd';
  ctx.fillRect(sx + 10, n.y + bob + 20, 16, 6);

  // Label
  ctx.shadowBlur = 0;
  ctx.font = 'bold 9px monospace';
  ctx.fillStyle = '#aaa';
  ctx.textAlign = 'center';
  ctx.fillText('OLD ASTRONAUT', sx + n.w / 2, n.y + bob - 8);

  if (showPrompt) {
    const pulse = 0.5 + 0.5 * Math.sin(time * 5);
    ctx.fillStyle = hexAlpha(NEON, pulse);
    ctx.font = 'bold 11px monospace';
    ctx.fillText('[ E ] talk', sx + n.w / 2, n.y + bob - 22);
  }

  ctx.restore();
}

// ─── End flag ────────────────────────────────────────────────────────────────
export function drawEndFlag(
  ctx:  CanvasRenderingContext2D,
  camX: number,
  time: number,
) {
  const sx = END_FLAG_X - camX;
  const sy = END_FLAG_Y;

  ctx.save();
  ctx.strokeStyle = '#aaa';
  ctx.lineWidth   = 3;
  ctx.beginPath();
  ctx.moveTo(sx, sy);
  ctx.lineTo(sx, sy + 160);
  ctx.stroke();

  ctx.shadowColor = NEON;
  ctx.shadowBlur  = 18;
  ctx.fillStyle   = NEON;
  const wave = Math.sin(time * 4) * 6;
  ctx.beginPath();
  ctx.moveTo(sx,        sy);
  ctx.lineTo(sx + 50,   sy + 10 + wave);
  ctx.lineTo(sx + 50,   sy + 30 + wave);
  ctx.lineTo(sx,        sy + 30);
  ctx.closePath();
  ctx.fill();

  ctx.font      = 'bold 13px monospace';
  ctx.fillStyle = '#fff';
  ctx.textAlign = 'center';
  ctx.fillText('FINISH', sx + 25, sy - 10);

  ctx.restore();
}

// ─── Checkpoint markers ──────────────────────────────────────────────────────
export function drawCheckpoints(
  ctx:         CanvasRenderingContext2D,
  checkpoints: { x: number; zoneId: number }[],
  activeZone:  number,
  camX:        number,
  _time:       number,
) {
  for (const cp of checkpoints) {
    const sx  = cp.x - camX;
    const active = cp.zoneId <= activeZone;
    const col    = active ? NEON : hexAlpha(NEON, 0.3);

    ctx.save();
    ctx.strokeStyle = col;
    ctx.shadowColor = col;
    ctx.shadowBlur  = active ? 14 : 4;
    ctx.lineWidth   = 2;

    ctx.beginPath();
    ctx.moveTo(sx,      GROUND_Y - 60);
    ctx.lineTo(sx + 12, GROUND_Y - 44);
    ctx.lineTo(sx,      GROUND_Y - 28);
    ctx.lineTo(sx - 12, GROUND_Y - 44);
    ctx.closePath();
    ctx.stroke();
    if (active) {
      ctx.fillStyle = hexAlpha(NEON, 0.25);
      ctx.fill();
    }
    ctx.restore();
  }
}

// ─── HUD ─────────────────────────────────────────────────────────────────────
export function drawHUD(
  ctx:       CanvasRenderingContext2D,
  W:         number,
  zone:      number,
  deaths:    number,
  progress:  number,
  hasJetpack: boolean,
) {
  ctx.save();
  ctx.font         = 'bold 13px monospace';
  ctx.fillStyle    = NEON;
  ctx.shadowColor  = NEON;
  ctx.shadowBlur   = 8;
  ctx.textAlign    = 'left';
  ctx.fillText(`ZONE ${zone}/5`, 16, 24);

  ctx.fillStyle   = '#ff4477';
  ctx.shadowColor = '#ff4477';
  ctx.textAlign   = 'right';
  ctx.fillText(`💀 ${deaths}`, W - 16, 24);

  if (hasJetpack) {
    ctx.fillStyle   = '#ffe066';
    ctx.shadowColor = '#ffe066';
    ctx.textAlign   = 'right';
    ctx.fillText('JETPACK', W - 16, 44);
  }

  const bw = 200;
  const bx = (W - bw) / 2;
  ctx.fillStyle = hexAlpha(NEON, 0.12);
  ctx.fillRect(bx, 10, bw, 8);
  ctx.shadowColor = NEON;
  ctx.shadowBlur  = 8;
  ctx.fillStyle   = NEON;
  ctx.fillRect(bx, 10, bw * progress, 8);

  ctx.restore();
}

// ─── Always-visible controls hint ────────────────────────────────────────────
export function drawControls(
  ctx: CanvasRenderingContext2D,
  W: number,
  H: number,
  touch: boolean,
  hasJetpack: boolean,
) {
  ctx.save();
  ctx.font = '11px monospace';
  ctx.fillStyle = 'rgba(200,200,224,0.55)';
  ctx.textAlign = 'left';

  const lines = touch
    ? [
        'JOYSTICK move',
        '↑ jump',
        'E talk / fly',
      ]
    : [
        'A D / ← →  move',
        'W / ↑ / SPACE  jump',
        hasJetpack ? 'HOLD E  jetpack' : 'E  talk / action',
      ];

  let y = H - 16 - (lines.length - 1) * 16;
  // Keep clear of mobile buttons on the right/left
  const x = touch ? W / 2 - 40 : 16;
  for (const line of lines) {
    ctx.fillText(line, x, y);
    y += 16;
  }
  ctx.restore();
}

// ─── Toast (non-pausing) ─────────────────────────────────────────────────────
export type ToastDraw = {
  title: string;
  lines: string[];
  alpha: number;
};

export function drawToast(
  ctx:   CanvasRenderingContext2D,
  W:     number,
  H:     number,
  toast: ToastDraw,
) {
  if (toast.alpha <= 0) return;

  const PAD = 16;
  const CW  = Math.min(420, W - 48);
  const lineH = 16;
  const CH  = PAD * 2 + 22 + toast.lines.length * lineH;
  const cx  = (W - CW) / 2;
  const cy  = 48;

  ctx.save();
  ctx.globalAlpha = toast.alpha;

  ctx.fillStyle   = 'rgba(4,4,16,0.88)';
  ctx.strokeStyle = NEON;
  ctx.lineWidth   = 1.5;
  ctx.shadowColor = NEON;
  ctx.shadowBlur  = 16;
  roundRect(ctx, cx, cy, CW, CH, 6);
  ctx.fill();
  ctx.stroke();
  ctx.shadowBlur = 0;

  ctx.font      = 'bold 13px monospace';
  ctx.fillStyle = NEON;
  ctx.textAlign = 'left';
  ctx.fillText(toast.title, cx + PAD, cy + PAD + 12);

  ctx.font      = '11px monospace';
  ctx.fillStyle = '#c8c8e0';
  let ty = cy + PAD + 32;
  for (const line of toast.lines) {
    ctx.fillText(line, cx + PAD, ty);
    ty += lineH;
  }

  ctx.restore();
}

// ─── "You died" splash ───────────────────────────────────────────────────────
export function drawDeath(
  ctx:   CanvasRenderingContext2D,
  W:     number,
  H:     number,
  alpha: number,
) {
  if (alpha <= 0) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle   = 'rgba(0,0,0,0.7)';
  ctx.fillRect(0, 0, W, H);

  ctx.font        = 'bold 48px monospace';
  ctx.fillStyle   = '#ff003c';
  ctx.shadowColor = '#ff003c';
  ctx.shadowBlur  = 28;
  ctx.textAlign   = 'center';
  ctx.fillText('YOU DIED', W / 2, H / 2);

  ctx.font      = '16px monospace';
  ctx.fillStyle = '#fff';
  ctx.shadowBlur = 0;
  ctx.fillText('respawning at checkpoint…', W / 2, H / 2 + 44);
  ctx.restore();
}

// ─── Win screen backdrop (links rendered as HTML overlay in Game.tsx) ────────
export function drawWin(
  ctx:   CanvasRenderingContext2D,
  W:     number,
  H:     number,
  alpha: number,
) {
  if (alpha <= 0) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = 'rgba(0,0,20,0.94)';
  ctx.fillRect(0, 0, W, H);
  ctx.restore();
}

// ─── Tiny util ───────────────────────────────────────────────────────────────
function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y,     x + w, y + r,     r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}
